import { jsx as _jsx } from "react/jsx-runtime";
/* Warning: please do not statically import any dependencies here except for static assets like icons */
import { ChartBarIcon } from "@panwds/icons";
import { lazy, Suspense, useCallback, useMemo } from "react";
import { history, getFeatureFlag, getAuthState, getCurrentTsgId, useStoreSelector, } from "@sparky/framework";
import { SPARKY_NAV3_LEFT_NAV_EXTPOINT, SPARKY_NAV3_ROUTES_EXTPOINT, } from "@sparky/framework/extpoints";
import CoPilotButton from "./components/CoPilotButton";
/**
 *
 * SASE IA Micro App Covers the following use cases
 * Use Case 1: Provide a stitched view for tab level integration with routes, TabListContainer
 * Use Case 2: Provide a Tenant vs Sub Tenant Selector for GCS vs Customer App
 * Use Case 3: Provide tenancy information in case of stitched or PAI exposed components
 *
 */
const loadApp = async () => {
    const CanvasAI = lazy(async () => import("./canvas-ai"));
    return {
        default: function CanvasAIWrapper() {
            const getAuthToken = useCallback(() => getAuthState()?.access_token ?? "", []);
            const navigate = useCallback((path) => history.push("/ai-canvas" + path), []);
            const getUser = useCallback(async () => getAuthState()?.user
                ? { username: getAuthState()?.user?.email }
                : undefined, []);
            const theme = useStoreSelector((state) => state?.theme);
            // biome-ignore lint/correctness/useExhaustiveDependencies: history.location included to ensure re-render on route change for canvas to canvas/:id
            const memoizedComponent = useMemo(() => (_jsx(Suspense, { fallback: null, children: _jsx(CanvasAI, { href: location.href, dependencies: {
                        navigate,
                        getUser,
                        getTsgId: () => getCurrentTsgId(),
                    }, routePrefix: "/ai-canvas", authToken: getAuthToken, theme: theme, enableTenantSelector: false, copilotButton: CoPilotButton }) })), 
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [getAuthToken, getUser, history.location, theme]);
            return memoizedComponent;
        },
    };
};
export const extensions = () => {
    const leftNavExt = {
        // declare ext for left nav tree
        point: SPARKY_NAV3_LEFT_NAV_EXTPOINT,
        configs: [
            {
                id: "copilot-data-studio.nav",
                config: {
                    stateLoaders: ["auth"],
                    navigation: () => [
                        {
                            key: "ai-canvas",
                            path: "/ai-canvas/canvas",
                            title: "AI Canvas",
                            icon: ChartBarIcon,
                        },
                    ],
                    isAllowed: () => getFeatureFlag?.("sparky.framework.netsec_dataspace_aicanvas"),
                },
            },
        ],
    };
    const routesExt = {
        // declare ext for routes
        point: SPARKY_NAV3_ROUTES_EXTPOINT,
        configs: [
            {
                id: "copilot-data-studio.routes.canvas",
                config: {
                    path: "/ai-canvas/canvas",
                    stateLoaders: ["auth"],
                    contentClassName: "canvas-ai-root panwds-tw3",
                    component: () => loadApp(), // any component must be dynamically import like this
                    isAllowed: () => getFeatureFlag?.("sparky.framework.netsec_dataspace_aicanvas"),
                },
            },
            {
                id: "copilot-data-studio.routes.widgets",
                config: {
                    path: "/ai-canvas/widgets",
                    stateLoaders: ["auth"],
                    contentClassName: "canvas-ai-root panwds-tw3",
                    component: () => loadApp(), // any component must be dynamically import like this
                    isAllowed: () => getFeatureFlag?.("sparky.framework.netsec_dataspace_aicanvas"),
                },
            },
        ],
    };
    return [leftNavExt, routesExt]; // return all extensions
};
export default function init() {
    return async function tsgLoad() {
        const navigation = [
            {
                key: "ai-canvas",
                title: "AI Canvas",
                expandable: false,
                path: "/ai-canvas/canvas",
                noRBACheck: true,
                exact: false,
                icon: ChartBarIcon, // either svg data url or panwds icon component, only needed in the first level
                mergeNav: true, // required if the first level is shared
                // in this example, the micro-app owns the first level, if the first level is shared, please have below in the 2nd level or whatever level it owns
                contentClassName: "canvas-ai-root panwds-tw3", // contentClassName should always besides the component, panwds-tw3 is needed for PanWDS
                component: () => loadApp(),
                avail: () => getFeatureFlag?.("sparky.framework.netsec_dataspace_aicanvas"),
            },
            {
                key: "widget-library",
                title: "Widget Library",
                expandable: false,
                hidden: true,
                path: "/ai-canvas/widgets",
                noRBACheck: true,
                exact: false,
                // in this example, the micro-app owns the first level, if the first level is shared, please have below in the 2nd level or whatever level it owns
                contentClassName: "canvas-ai-root panwds-tw3", // contentClassName should always besides the component, panwds-tw3 is needed for PanWDS
                component: () => loadApp(),
                avail: () => getFeatureFlag?.("sparky.framework.netsec_dataspace_aicanvas"),
            },
        ];
        return { navigation };
    };
}
